import { downloadPyodide, preparePyodide, TTY, Pyodide } from './pyodide'

const chunks: string[] = []
let lastPost = 0
let updateOut: ((data: string[]) => void) | null = null
const decoder = new TextDecoder()

function print(tty: TTY) {
  if (tty.output && tty.output.length > 0) {
    const arr = new Uint8Array(tty.output)
    chunks.push(decoder.decode(arr))
    tty.output.length = 0
    const now = performance.now()
    if (now - lastPost > 100) {
      update()
      lastPost = now
    }
  }
}

function update() {
  if (updateOut) {
    updateOut(chunks)
  }
  chunks.length = 0
}

function log(msg: string) {
  console.debug('log:', msg)
  if (updateOut) {
    updateOut([msg + '\n'])
  }
}

interface PyodideWrapper {
  pyodide: Pyodide
  reformatException: () => string
}

let _pyodideWrapper: PyodideWrapper | null = null

async function load(dependencies: string[]) {
  if (_pyodideWrapper === null) {
    console.debug('Downloading pyodide...')

    const pyodide = await downloadPyodide()
    preparePyodide(pyodide, print)

    console.debug('Loading micropip...')
    await pyodide.loadPackage(['micropip'])
    const micropip = pyodide.pyimport('micropip')
    const other_deps = dependencies.filter(d => !d.startsWith('pydantic-core'))

    console.debug(`Installing ${other_deps}...`)
    await micropip.install(other_deps)

    await pyodide.runPythonAsync(
      // language=python
      `
def reformat_exception():
    import sys
    from traceback import format_exception
    # Format a modified exception here
    # this just prints it normally but you could for instance filter some frames
    lines = format_exception(sys.last_type, sys.last_value, sys.last_traceback)
    # remove the traceback line about running pyodide
    lines.pop(1)
    lines.pop(1)
    return ''.join(lines)
`,
    )
    _pyodideWrapper = {
      pyodide,
      reformatException: pyodide.globals.get('reformat_exception'),
    }
  }
  return _pyodideWrapper
}

export async function runCode(
  code: string,
  onMessage: (data: string[]) => void,
  dependencies: string[],
): Promise<void> {
  updateOut = onMessage
  let py: PyodideWrapper
  try {
    py = await load(dependencies)
  } catch (e) {
    update()
    log(`Error starting Python: ${e}`)
    updateOut = null
    throw e
  }
  await py.pyodide.runPythonAsync(`
code = b"""
STA_IF = 0
AP_IF = 1

class WLAN:
  def __init__(self, iface):
    self.iface = iface
    self.active_status = False
    self.connected_status = False
    self.ssid = None
    self.password = None
    self.ifconfig_settings = ('192.168.1.4', '255.255.255.0', '192.168.1.1', '8.8.8.8')

  def active(self, is_active=None):
    if is_active is not None:
      self.active_status = is_active
    return self.active_status

  def connect(self, ssid, password):
    if not self.active_status:
      raise RuntimeError("Interface not active")
    self.ssid = ssid
    self.password = password
    self.connected_status = True

  def disconnect(self):
    if not self.active_status:
      raise RuntimeError("Interface not active")
    self.connected_status = False
    self.ssid = None
    self.password = None

  def isconnected(self):
    return self.active_status and self.connected_status

  def ifconfig(self, config=None):
    if config:
      self.ifconfig_settings = config
    return self.ifconfig_settings

  def scan(self):
    if not self.active_status:
      raise RuntimeError("Interface not active")
    # This is a fake scan function, it returns a list of tuples with SSID and info
    fake_scan_results = [(b'mon reseau', b'0e9491d9aa97', 8, -61, 3, False), (b'un autre reseau', b'b07fb9a06870', 8, -86, 3, False)]
    print(fake_scan_results)
    return fake_scan_results

class network:
  WLAN = WLAN

  def __init__(self):
    pass

  def __str__(self):
    return "This is a dummy network instance."
"""
with open("network.py", "wb") as f:
  f.write(code)`)
  await py.pyodide.runPythonAsync(`
code = b"""
class Pin:
  IN = 0
  OUT = 1
  OPEN_DRAIN = 2
  ALT = 3
  ALT_OPEN_DRAIN = 4

  PULL_UP = 1
  PULL_DOWN = 2
  PULL_HOLD = 3

  IRQ_FALLING = 1
  IRQ_RISING = 2
  IRQ_LOW_LEVEL = 4
  IRQ_HIGH_LEVEL = 8

  def __init__(self, pin, mode=-1, pull=-1, value=None, alt=-1):
    self.pin = pin
    self.mode = mode
    self.pull = pull
    self.value = value
    self.alt = alt
    print(f"Pin {pin} initialized with mode={mode}, pull={pull}, value={value}, alt={alt}")

  def init(self, mode=-1, pull=-1, value=None, alt=-1):
    self.mode = mode
    self.pull = pull
    self.value = value
    self.alt = alt
    print(f"Pin {self.pin} re-initialized with mode={mode}, pull={pull}, value={value}, alt={alt}")

  def value(self, x=None):
    if x is None:
      print(f"Getting value of Pin {self.pin}")
      return self.value
    else:
      self.value = x
      print(f"Setting value of Pin {self.pin} to {x}")

  def on(self):
    self.value = 1
    print(f"Pin {self.pin} set to HIGH")

  def off(self):
    self.value = 0
    print(f"Pin {self.pin} set to LOW")

  def irq(self, handler=None, trigger=IRQ_FALLING | IRQ_RISING, *, priority=1, wake=None, hard=False):
    print(f"Configuring IRQ for Pin {self.pin} with handler={handler}, trigger={trigger}, priority={priority}, wake={wake}, hard={hard}")

def freq():
  return 160000000  # Example frequency

def reset():
  print("Device is resetting...")

def idle():
  print("Device is idling...")

def sleep():
  print("Device is sleeping...")

def deepsleep():
  print("Device is in deep sleep mode...")
"""
with open("machine.py", "wb") as f:
  f.write(code)`)
  await py.pyodide.runPythonAsync(`
code = b"""
class thingz:
  def __init__(self):
    pass

  def __str__(self):
    return "This is a dummy thingz instance."

  class Led:
    def __init__(self):
      self._red = 0
      self._green = 0
      self._blue = 0

    def set_colors(self, red: int, green: int, blue: int) -> None:
      self.set_red(red)
      self.set_green(green)
      self.set_blue(blue)
      print(f"Galaxia RGB led set to ({self._red}, {self._green}, {self._blue})")

    def set_red(self, red: int) -> None:
      if 0 <= red <= 255:
        self._red = red
      else:
        raise ValueError("Red value must be between 0 and 255")

    def set_green(self, green: int) -> None:
      if 0 <= green <= 255:
        self._green = green
      else:
        raise ValueError("Green value must be between 0 and 255")
      
    def set_blue(self, blue: int) -> None:
      if 0 <= blue <= 255:
        self._blue = blue
      else:
        raise ValueError("Blue value must be between 0 and 255")

    def get_red(self) -> int:
      return self._red

    def get_green(self) -> int:
      return self._green

    def get_blue(self) -> int:
      return self._blue

    def read_light_level(self) -> int:
      pass  # implementation not provided

  class Button:
    def __init__(self):
      self.pressed = False
      self.presses = 0
      self.callback = None

    def is_pressed(self) -> bool:
      return self.pressed

    def was_pressed(self) -> bool:
      if self.pressed:
        self.pressed = False
        return True
      return False

    def get_presses(self) -> int:
      return self.presses

    def on_pressed(self, callback: callable) -> None:
      self.callback = callback

    def set_pressed(self, state: bool) -> None:
      self.pressed = state
      if state and self.callback:
        self.callback()
      if state:
        self.presses += 1

    def get_pressed(self) -> bool:
      return self.pressed

led = thingz.Led()
button_a = thingz.Button()
button_b = thingz.Button()
touch_n = thingz.Button()
touch_s = thingz.Button()
touch_e = thingz.Button()
touch_w = thingz.Button()
"""
with open("thingz.py", "wb") as f:
  f.write(code)`)
  await py.pyodide.runPythonAsync(`
import micropip
await micropip.install("requests")`)
  try {
    await py.pyodide.runPythonAsync(code)
    update()
  } catch (e) {
    update()
    log(py.reformatException())
  }
  updateOut = null
}
